import { Icons, ProgressBar } from '@mnd-frontend/ui/publish/components';
import {
  GREY_200,
  RED_700,
  SPACING_0_5,
  SPACING_1,
  SPACING_2,
  SPACING_3,
} from '@mnd-frontend/ui/shared/tokens';
import type { FC } from 'react';
import { type ForwardedRef, forwardRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const Wrapper = styled.div`
  width: 23.125rem;
  padding: ${SPACING_3};
  border-radius: 0.5rem;
  box-shadow:
    0px 0px 2px rgba(49, 73, 164, 0.14),
    0px 0px 3px rgba(49, 73, 164, 0.1),
    0px 12px 16px rgba(49, 73, 164, 0.08);
  background-color: ${GREY_200};
`;

const Header = styled.div`
  display: grid;
  justify-content: space-between;
  align-items: center;
  grid-template-columns: auto 6rem;
  gap: ${SPACING_0_5};
  margin-bottom: ${SPACING_2};

  h4 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
  }

  p {
    margin-bottom: ${SPACING_1};
  }
`;

const CTAWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: ${SPACING_2};
  padding: ${SPACING_2};
  border-radius: 0.25rem;
  background: #fff;
  gap: 0.875rem;
  button {
    margin-top: ${SPACING_1};
  }
  i {
    color: ${RED_700};
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  svg {
    width: 1.5rem;
  }

  p {
    margin: 0;
  }
`;

type Props = {
  trialDaysLeft?: number;
  totalTrialDays?: number;
  children?: ReactNode;
  header: ReactNode;
  body: ReactNode;
  hideProgress?: boolean;
  usps: { icon: keyof typeof Icons; text: string }[];
};

const InnerTrialPopover = (
  { trialDaysLeft = 0, totalTrialDays = 0, children, header, body, usps, hideProgress }: Props,
  ref: ForwardedRef<HTMLDivElement>,
) => {
  const { t } = useTranslation();

  return (
    <Wrapper ref={ref}>
      <Header>
        <h4>{header}</h4>
        {!hideProgress && trialDaysLeft >= 0 && (
          <ProgressBar
            percent={(totalTrialDays - trialDaysLeft) * totalTrialDays}
            label={`${trialDaysLeft} ${t('trial_days_left')}`}
          />
        )}
      </Header>
      <p>{body}</p>
      <CTAWrapper>
        {usps.map((usp, i) => {
          const Icon = Icons[usp.icon] as FC<{ variant?: string }>;
          return (
            <Row key={i}>
              <Icon />
              <p>{usp.text}</p>
            </Row>
          );
        })}
        {children}
      </CTAWrapper>
    </Wrapper>
  );
};

export const TrialPopover = forwardRef<HTMLDivElement, Props>(InnerTrialPopover);
